import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";

import { MenuOptionSpec } from "@transfr-inc/dashboard-components";
import {
  Column,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import {
  Button,
  SearchBox,
  useSearch,
} from "@transfr-inc/dashboard-components/forms";

import {
  RecordStatus,
  RecordStatusDropdown,
} from "@components/custom/dropdowns";
import { Role } from "@models/role";
import { useFeatures } from "@queries";
import { RosterModal } from "@views/users/roster/roster-modal";

import {
  DataTableColumnDefinition,
  getUserDetailPathConfig,
} from "./data-table-column-definition";
import { ZeroStateMessage } from "./zero-state-message";

import "./trainee-users-tab.scss";

const hiddenOptions = new Set(["edit", "change_password"]);
const archivedAllowedOptions = new Set(["unarchive", "delete"]);
// Options that aren't supported for integration-managed orgs.
const integrationGatedOptions = new Set(["archive", "unarchive", "delete"]);

export function TraineeUsersTab({
  data,
  bottomBarOptions,
  actionMenuOptions,
  responsiveStyles = {},
  dataFiltered,
  className = "trainee-users-tab",
  statusFilter = RecordStatus.Active,
  onStatusFilterChange,
  showStatusDropdown,
  archiveUserEnabled,
  isIntegrationOrgCode = () => false,
}) {
  const features = useFeatures();
  const { canCreateUser } = features || {};

  const breadcrumbLabel = "All Users";
  const hasBulkActions = bottomBarOptions?.length > 0;
  const columns = [
    ...(hasBulkActions
      ? [new Column("select", /*title:*/ undefined, ColumnType.SELECTION)]
      : []),
    DataTableColumnDefinition.AVATAR,
    DataTableColumnDefinition.LAST_NAME(
      getUserDetailPathConfig({ breadcrumbLabel })
    ),
    DataTableColumnDefinition.FIRST_NAME(
      getUserDetailPathConfig({ breadcrumbLabel })
    ),
    DataTableColumnDefinition.ORGANIZATION,
    DataTableColumnDefinition.CLASSROOM,
    DataTableColumnDefinition.USERNAME,
    DataTableColumnDefinition.EMAIL,
    DataTableColumnDefinition.LAST_LOGIN,
  ];

  const [stateTrainees, setStateTrainees] = useState(data);
  const [searchActive, setSearchActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openRosterModal, setOpenRosterModal] = useState(false);

  const nameSearch = useSearch({
    data: data ?? [],
    searchKeys: ["firstName", "lastName", "username"],
  });

  const onResultSelected = (option) => {
    setStateTrainees([option]);
  };

  const onNameTermChange = (term) => {
    setSearchActive(term.length > 0);
    setSearchTerm(term);
  };

  const filterRowMenuOptions = useCallback(
    (rowData, menuOptions) => {
      let options = menuOptions || [];
      // A learner in an integration-managed child org can't be archived,
      // unarchived, or deleted, even when the list spans other orgs.
      if (isIntegrationOrgCode(rowData?.organizationCode)) {
        options = options.filter(
          (option) => !integrationGatedOptions.has(option?.id)
        );
      }
      if (rowData?.isAnonymous) {
        options = options.filter((option) => !hiddenOptions.has(option?.id));
      }
      if (rowData?.isArchived) {
        options = options.filter((option) =>
          archivedAllowedOptions.has(option?.id)
        );
      } else {
        options = options.filter(
          (option) =>
            option?.id !== "unarchive" &&
            (!archiveUserEnabled || option?.id !== "delete")
        );
      }
      return options;
    },
    [archiveUserEnabled, isIntegrationOrgCode]
  );

  useEffect(() => {
    setStateTrainees(nameSearch.queryData(searchTerm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const results = nameSearch.updateData(data);
    setStateTrainees(results);
  }, [data]);

  useEffect(() => {
    setSearchTerm("");
    setSearchActive(false);
  }, [statusFilter]);

  const zeroStateMessage = (
    <ZeroStateMessage
      role={Role.STUDENT}
      filtered={searchActive || dataFiltered}
    ></ZeroStateMessage>
  );

  // TODO: Remove this once Users page uses the library data table version
  const actionOptions = actionMenuOptions
    ?.filter((option) => option)
    .map((option) => {
      const { icon, text, targetFunction, id } = option ?? {};
      return new MenuOptionSpec(text, icon, targetFunction, id);
    });

  if (!stateTrainees) {
    return;
  }

  return (
    <div className="trainee-users-tab">
      <div className="trainee-users-tab-header-section">
        <SearchBox
          placeholder={"Search"}
          query={nameSearch.queryData}
          onSearchTermChange={onNameTermChange}
          onResultSelected={onResultSelected}
          // The label is a composite the name query can't match, so a pick
          // must not be re-run as a search term (it would blank the list).
          searchOnSelect={false}
          customOptionLabel={(r) =>
            `${r?.lastName}, ${r?.firstName} (${r?.username})`
          }
          searchTerm={searchTerm}
          className={"trainee-users-search"}
        ></SearchBox>
        {showStatusDropdown && (
          <RecordStatusDropdown
            className="learner-status-dropdown"
            selected={statusFilter}
            onChange={onStatusFilterChange}
          />
        )}
        {canCreateUser && (
          <Button
            icon={["fa-regular", "clipboard-list-check"]}
            onClick={() => setOpenRosterModal(true)}
            size="small"
          >
            Roster
          </Button>
        )}
      </div>
      <div className="trainees-table-container ">
        <DataTable
          columns={columns}
          data={stateTrainees}
          className={clsx(
            "trainee-users-table",
            className,
            hasBulkActions && "selection"
          )}
          keyFields={["id"]}
          keyPrefix="trainee-users"
          defaultSortPropName={"isAnonymous"}
          defaultSortDirection={SortDirection.ASCENDING}
          zeroStateMessage={zeroStateMessage}
          responsiveStyles={responsiveStyles}
          bottomBarOptions={bottomBarOptions}
          tableContentName={{ singular: "user", plural: "users" }}
          actionMenuOptions={actionOptions}
          virtualRowEnabled
          isMenuActionVisible={filterRowMenuOptions}
          isRowLocked={(row) => isIntegrationOrgCode(row?.organizationCode)}
        />
      </div>
      <RosterModal
        onClose={() => setOpenRosterModal(false)}
        show={openRosterModal}
        responsiveStyles={responsiveStyles}
      ></RosterModal>
    </div>
  );
}
